import { Button, Col, Form, Row, Switch, Tooltip } from 'antd';
import _ from 'lodash';
import React from 'react';
import { connect } from 'react-redux';
import { AnyAction } from "redux";
import { ThunkDispatch } from "redux-thunk";
import GenderSelector from '../../components/create/GenderSelector';
import AgeSelector from '../../components/create/AgeSelector';
import SubICBLocationSelector from '../../components/create/SubICBLocationSelector';
import DiagnosisTree from '../../components/create/DiagnosisTree';
import HRGSelector from '../../components/create/HRGsSelector';
import ProceduresSelector from '../../components/create/ProceduresSelector';
import TrustSelector from '../../components/create/TrustSelector';
import TreatmentSelector from '../../components/create/TreatmentSelector';
import { AppState } from '../../types/AppState';
import { filterClose, loadPathwayMetrics, upateFilterOnDestinationMetric, upateFilterOnEvent } from './actions';
import { getDatasetTheme } from './PathwayTheme';
import { emptyPathwayFilter, findPathway } from './reducers';
import { CohortPathway, DestinationFilter, EventFilter } from './types/CohortPathway';
import { isDestinationFilter, isEventFilter } from './types/EventMetric';

const datasets = [
    { title: "A&E", value: "ae", id: 1 },
    { title: "Inpatient Non-Elective", value: "nel", id: 2 },
    { title: "Inpatient Elective", value: "el", id: 3 },
    { title: "Outpatient", value: "op", id: 4 }
]

interface Props {
    currentFilter: EventFilter | DestinationFilter
    pathway: CohortPathway,
    selectedQueryId: number,
    applyAndCloseFilter(filter: EventFilter | DestinationFilter): void
    closeFilter(): void
    icd10tree: []
    procedureTree: []
}

const PathwayFilters = ({ currentFilter, pathway, selectedQueryId, applyAndCloseFilter, closeFilter, icd10tree, procedureTree }: Props) => {

    const [form] = Form.useForm()

    const filter = Object.assign({}, currentFilter.filter)

    if (isEventFilter(currentFilter)) {
        (currentFilter as EventFilter).datasets.forEach(i => {
            const dataset = _.find(datasets, { id: i })
            // @ts-ignore
            filter[dataset.value] = true
        })
    }

    const onFinish = (values: any) => {
        const updatedCurrentFilter = Object.assign({}, currentFilter, {
            filter: values
        })

        if (isEventFilter(currentFilter)) {
            const result = form.getFieldsValue([...datasets.map(i => i.value)])
            // @ts-ignore
            updatedCurrentFilter.datasets = datasets
                .map(i => {
                    if (!_.isNil(result[i.value]) && result[i.value] === true) {
                        return i.id
                    } else {
                        return undefined
                    }
                })
                .filter(i => !_.isNil(i))
        }

        applyAndCloseFilter(updatedCurrentFilter)
    };

    const individualFilters = () => {
        return datasets.map(i => {
            return <Form.Item key={i.id} name={i.value} label={i.title} valuePropName="checked">
                <Switch />
            </Form.Item>
        })
    }

    const eventFilterOptions = () => {
        if (isEventFilter(currentFilter)) {
            return <span>
                {individualFilters()}
            </span>
        } else {
            return null
        }
    }

    const reset = () => {
        const datasetOptions = datasets.reduce((total, v) => {
            // @ts-ignore
            total[v.value] = true
            return total
        }, {})

        form.setFieldsValue({
            ...datasetOptions,
            ...emptyPathwayFilter()
        });
    }

    return (
        <Row className="pathway-filter">
            <Col span={24}>
                <div>
                    <span>Event {currentFilter.eventNo}{isDestinationFilter(currentFilter) ? <span>, {getDatasetTheme((currentFilter as DestinationFilter).dataset).title}</span> : null}</span>
                    <Form
                        name="filters"
                        form={form}
                        onFinish={onFinish}
                        initialValues={filter}
                        scrollToFirstError
                    >
                        {eventFilterOptions()}

                        <DiagnosisTree
                            form={form}
                            fieldId="primaryDiagnosis"
                            fieldName="Primary Diagnosis"
                            fieldPlaceHolder="Select Primary Diagnosis"
                            isDisabled={false}
                            className="no-margin-bottom"
                            icd10tree={icd10tree}
                        />

                        <Tooltip title="Relationship between Primary & Secondary diagnosis.">
                            <Form.Item
                                name="diagOr"
                                label=""
                                rules={[]}
                                valuePropName="checked"
                                className="no-margin-bottom"
                            >
                                <Switch checkedChildren="OR" unCheckedChildren="AND" size="small" />
                            </Form.Item>
                        </Tooltip>

                        <DiagnosisTree
                            form={form}
                            fieldId="secondaryDiagnosis"
                            fieldName="Secondary Diagnosis"
                            fieldPlaceHolder="Select Secondary Diagnosis"
                            isDisabled={false}
                            icd10tree={icd10tree}
                        />

                        <TrustSelector form={form} />

                        <ProceduresSelector
                            form={form}
                            procedureTree={procedureTree}
                        />

                        <HRGSelector form={form} />

                        <SubICBLocationSelector form={form} />

                        <TreatmentSelector form={form} />

                        <AgeSelector form={form} />

                        <GenderSelector form={form} />

                        <Form.Item >
                            <Button type="primary" htmlType="submit">Apply</Button>
                            <Button onClick={reset}>Clear</Button>
                            <Button onClick={closeFilter}>Close</Button>
                        </Form.Item>
                    </Form>
                </div>
            </Col>
        </Row>
    )
}

const mapStateToProps = (state: AppState, ownProps: any): Props => {
    const pathway = findPathway(state.pathway.pathways, state.pathway.pathwayId!)
    return {
        pathway: pathway,
        selectedQueryId: state.current.queryId,
        currentFilter: state.pathway.currentFilter,
        icd10tree: state.settings.values.ICD10TREE,
        procedureTree: state.settings.values.PROCEDURETREE,
        ...ownProps
    }
}

const mapDispatchToProps = (dispatch: ThunkDispatch<any, any, AnyAction>) => {
    return {
        applyAndCloseFilter: (filter: EventFilter | DestinationFilter) => {
            if (isEventFilter(filter)) {
                dispatch(upateFilterOnEvent(filter as EventFilter))
            } else {
                dispatch(upateFilterOnDestinationMetric(filter as DestinationFilter))
            }
            dispatch(filterClose())
            dispatch(loadPathwayMetrics())
        },
        closeFilter: () => {
            dispatch(filterClose())
        }
    }
}

export default connect(mapStateToProps, mapDispatchToProps)(PathwayFilters)